import getValue from '@visa/toolkit/getDeepValue';
import jsCookie from './jsc-converter';
let store = undefined;
// previous `config.jsp` params mapped via embedded global data object
const windowMappings = {
    euCountryCodes: 'visa.config.application.euroCountries',
    euIP: 'visa.config.application.isEuroIp',
    honorConsentCookieForOptin: 'visa.config.application.honorConsentCookieForOptin',
    isCookieConsentEnabled: 'visa.config.application.isCookieConsentEnabled',
    policyVersion: 'visa.config.application.policyVersion'
};
// current `config.jsp` loading values into redux state - defaults, overrideable
let stateMappings = {
    euCountryCodes: 'euCountryCodes',
    euIP: 'euIP',
    honorConsentCookieForOptin: 'honorConsentCookieForOptin',
    isCookieConsentEnabled: 'isCookieConsentEnabled',
    partnerCountry: 'partnerCountry',
    policyVersion: 'policyVersion',
    userCountryCode: 'userCountryCode'
};
/**
 * Returns flag value from redux store or global `visa` data object
 * @param {!string} key
 * @return {!any} `false` if value is undef
 */
const _getMappedValue = key => {
    if (store && store.getState) {
        return getValue(store.getState(), stateMappings[key]) || false;
    }
    return getValue(window, windowMappings[key]) || false;
};
/**
 * This utility `connect()'s` this utility to the redux state
 * @param {!object} str
 * @param {?object} mapping (optional) KVP with `key` being the variable name, `value` being the path in the state.
 *                        Defaults to `key` as top-level in state
 */
export const setStore = (str, mapping) => {
    store = str;
    stateMappings = mapping || stateMappings;
};
/**
 * `honorConsentCookieForOptin` flag used by branded and federation to alter canDropCookie logic.
 * User-configured experience (always prompt for consent).
 * For EU cookie compliance.
 * @return {!boolean}
 */
export const getHonorConsentCookieForOptin = () => _getMappedValue('honorConsentCookieForOptin');
/**
 * Fetches cookie consent policyVersion
 * @return {!number} null if key not present.
 */
export const getPolicyVersion = () => _getMappedValue('policyVersion') || null;
/**
 * Determine the application, supports 'is cookie consent feature enabled'?
 * @return {!boolean}
 */
export const isCookieConsentEnabled = () => _getMappedValue('isCookieConsentEnabled');
/**
 * Checks that user has consented and that the policy version is current
 * @return {!boolean}
 */
export const hasConsented = () => {
    let c = jsCookie.get('hasConsented');
    if (!c) {
        return false;
    }
    c = JSON.parse(c);
    /* istanbul ignore next */
    return c.policyVersion === getPolicyVersion() && c.consent === true;
};
/**
 * Checks that user has optedIn and that the policy version is current
 * @return {!boolean}
 */
export const hasOptedIn = () => {
    let c = jsCookie.get('hasConsented');
    if (!c) {
        return false;
    }
    c = JSON.parse(c);
    /* istanbul ignore next */
    return c.policyVersion === getPolicyVersion() && c.optedIn === true;
};
/**
 * @param {!string} countryCode    ie: US, FR, GB
 * @return {boolean}
 */
export const isEuroCountry = countryCode => {
    if (countryCode && typeof countryCode === 'string') {
        return _getMappedValue('euCountryCodes').indexOf(countryCode.toUpperCase()) > -1;
    }
    return false;
};
/**
 * @param {string} country ie: "US", "FR", "GB" - optional value comes from canDropCookie.
 *
 * @return {boolean}
 */
export const isEuroUser = country => {
    // TODO add defenses to properly determine country code format.
    let userCountryCode = _getMappedValue('userCountryCode');
    if (country) {
        userCountryCode = country;
    }
    return (_getMappedValue('euIP') ||
        isEuroCountry(_getMappedValue('partnerCountry')) ||
        isEuroCountry(userCountryCode));
};
/**
 * To determine, the app can drop non-essential cookies for the European users
 * Conditions:
 *    1. Check for honorConsentCookieForOptin flag - if `true` and no hasConsented cookie return optedIn flag directly
 *    2. If the hasConsented cookie is exists and
 *       the user has accepted the cookie policy
 *    3. If the hasConsented cookie is not exists (condition2), then check the user is European
 *        and he/she accepted the policy
 *    4. Default return true - Assumption Non-EU User
 * Requirement: App should check the hasConsented cookie present and the user preference about cookie policy acceptance,
 * then check the user locations whether he is from European customer.
 * @param {string} country - optional value comes from LOCALE_TYPES.SET_LOCALE reducer
 * @return {!boolean}
 */
export const canDropCookie = country => {
    const optedIn = hasOptedIn();
    const consent = hasConsented();
    if (getHonorConsentCookieForOptin() && !consent) {
        return optedIn;
    }
    // Look at the conditions in the above comments
    if (isCookieConsentEnabled() && (consent || isEuroUser(country))) {
        return optedIn;
    }
    return true;
};
/**
 * GetDomainName
 * @param {!string} domainName  The domainname containing the hostname of our application
 * @param {!string} isSecureCheckout  If true, returns '.secure.checkout.visa.com' for visa.com hosts
 * @return {string} cookieDomain  domain of the cookie
 */
export const getDomainName = (domainName, isSecureCheckout = false) => {
    const k8sDomain = '.k8s-np-cls1-p.trusted.visa.com';
    const dockerDomain = '.oce-np-sm-ddp-p-en.trusted.visa.com';
    if (domainName && typeof domainName === 'string') {
        // k8s domain
        if (domainName.indexOf(k8sDomain) > -1) {
            return k8sDomain;
        }
        // domains ran in docker container use differ domain than `secure.checkout.visa.com`
        if (domainName.indexOf(dockerDomain) > -1) {
            return dockerDomain;
        }
        // visa.com domains
        else if (domainName.indexOf('visa.com') > -1) {
            // [VCO-7726], [VCO-7809] & [F3049]
            // - match behavior in RXO - lower env subdomains partition usage in testing deeplinking
            return isSecureCheckout ? '.secure.checkout.visa.com' : '.visa.com';
        }
        else if (domainName.indexOf('digitalcardservice.com') >= 0) {
            return '.digitalcardservice.com';
        }
    }
    // broadDomain default
    return '.www.v.me';
};
//# sourceMappingURL=helpers.js.map